import React, { useState, useEffect } from 'react';
import { refereesAPI } from '../../services/api';

const RefereesSettings = () => {
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingReferee, setEditingReferee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    published: true
  });

  useEffect(() => {
    fetchReferees();
  }, []);

  const fetchReferees = async () => {
    try {
      const response = await refereesAPI.getAll();
      setReferees(response.data);
    } catch (err) {
      setError('Failed to fetch referees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReferee) {
        await refereesAPI.update(editingReferee._id, formData);
      } else {
        await refereesAPI.create(formData);
      }
      fetchReferees();
      resetForm();
    } catch (err) {
      setError('Failed to save referee');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this referee?')) {
      try {
        await refereesAPI.delete(id);
        fetchReferees();
      } catch (err) {
        setError('Failed to delete referee');
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', published: true });
    setEditingReferee(null);
    setShowModal(false);
  };

  const handleEdit = (referee) => {
    setFormData({
      name: referee.name,
      published: referee.published
    });
    setEditingReferee(referee);
    setShowModal(true);
  };

  if (loading) return <div className="loading loading-spinner loading-lg"></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Referees</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
        >
          Add Referee
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(referees) && referees.map((referee) => (
              <tr key={referee._id}>
                <td>{referee.name}</td>
                <td>
                  <div className={`badge ${referee.published ? 'badge-success' : 'badge-error'}`}>
                    {referee.published ? 'Published' : 'Draft'}
                  </div>
                </td>
                <td>{new Date(referee.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="space-x-2">
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleEdit(referee)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-error"
                      onClick={() => handleDelete(referee._id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {editingReferee ? 'Edit Referee' : 'Add New Referee'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input input-bordered"
                  required
                />
              </div>
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Published</span>
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="checkbox"
                  />
                </label>
              </div>
              <div className="modal-action">
                <button type="submit" className="btn btn-primary">
                  {editingReferee ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default RefereesSettings;
